import {
  Box,
  Button,
  CopyToClipboard,
  SpaceBetween,
  Modal,
  Alert,
  Select,
} from "@amzn/awsui-components-react-v3";
import React, { FormEvent, useEffect, useState } from "react";
import { useSendRegistrationNotificationMutation } from "../services/idp/idp";
import { Form, FormField } from "@amzn/awsui-components-react-v3/polaris";

interface SendRegistrationCodeProps {
  alias: string;
  visible: boolean;
  onDismiss: () => void;
}

interface SendRegistrationCodeFormProps {
  template: { label?: string; value?: string };
}

// 6/18/26: the main use case for registration codes is now KCE-Low MacOS registration
const initialFormState: SendRegistrationCodeFormProps = {
  template: {
    label: "KCE-Low MacOS Registration",
    value: "idp-registration-kce-low-macos",
  },
};

export const SendRegistrationCode = ({
  alias,
  visible,
  onDismiss,
}: SendRegistrationCodeProps) => {
  const [
    sendRegNotification,
    { data, isLoading, isError, error, isSuccess, reset },
  ] = useSendRegistrationNotificationMutation();
  const [formState, setFormState] = useState(initialFormState);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (isError) {
      const errorAsAny = error as any;
      const errorMessage =
        errorAsAny?.data?.detail ?? errorAsAny?.data?.message ?? "";
      setFormError(errorMessage);
    }
  }, [isError, error]);

  const sendRegistrationNotificationToUser = (
    e: FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    sendRegNotification({
      alias,
      notificationTemplateName: formState.template.value ?? "",
    });
  };

  const closeModal = () => {
    onDismiss();
    reset();
  };

  const formAlert = () => {
    if (!!data?.registrationLink) {
      return (
        <Alert
          type="error"
          action={
            <CopyToClipboard
              copyButtonText="Copy"
              copyErrorText="Failed to copy link to clipboard"
              copySuccessText="Successfully copied link to clipboard"
              textToCopy={data?.registrationLink}
            />
          }
          header="Unable to send registration code"
        >
          Click the &apos;Copy&apos; button and send the link to{" "}
          {alias || "this user"} via your corporate email, Slack, or Chime.
        </Alert>
      );
    } else if (error) {
      return (
        <Alert type="error" header="Failed to send registration code">
          {formError}
        </Alert>
      );
    } else if (isSuccess) {
      return (
        <Alert type="success">
          Successfully sent a registration code to {alias || "this user"}.
        </Alert>
      );
    } else {
      return <></>;
    }
  };

  return (
    <Modal
      onDismiss={closeModal}
      visible={visible}
      header="Send registration code"
      data-testid={`send-registration-code-modal-${alias}`}
    >
      <SpaceBetween size="l">
        {formAlert()}
        <Box variant="p">
          You are sending a registration code to <strong>{alias}</strong>.
          Select from the available templates below and then click
          &quot;Send&quot;. The code will be delivered via Amazon email.
        </Box>
        <form onSubmit={(e) => sendRegistrationNotificationToUser(e)}>
          <Form
            actions={
              <Button
                disabled={isLoading}
                loading={isLoading}
                variant="primary"
                data-testid="send-registration-code-modal-submit-button"
              >
                Send
              </Button>
            }
          >
            <FormField label="Notification template">
              {/*TODO dynamically load template options (https://jira.kbobjects.com/browse/KIIP-225)*/}
              <Select
                selectedOption={formState.template}
                options={[
                  {
                    label: "KCE-Low MacOS Registration",
                    value: "idp-registration-kce-low-macos",
                  },
                  {
                    label: "Default Security Key Registration",
                    value: "idp-registration-default",
                  },
                  {
                    label: "Handheld Scanners OTP Registration",
                    value: "idp-registration-handhelds",
                  },
                  {
                    label: "KCE-Moderate Key Registration",
                    value: "idp-registration-kce-moderate",
                  },
                ]}
                onChange={({ detail }) => {
                  setFormState({
                    ...formState,
                    template: detail.selectedOption,
                  });
                  reset();
                }}
              />
            </FormField>
          </Form>
        </form>
      </SpaceBetween>
    </Modal>
  );
};
